var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useContext } from 'react';
import Flagship from '@flagship.io/js-sdk';
import { FlagshipContext } from './FlagshipContext';
import { logError, logWarn } from './utils';
import { Flag } from './Flag';
import { noVisitorDefault, noVisitorMessage } from './constants';
const checkType = (value, defaultValue) => (typeof value === 'object' &&
    typeof defaultValue === 'object' &&
    Array.isArray(value) === Array.isArray(defaultValue)) ||
    typeof value === typeof defaultValue;
const fsModificationsSync = (args) => {
    const { visitor, params, activateAll, state, functionName, config } = args;
    if (visitor) {
        return visitor.getModificationsSync(params, activateAll);
    }
    const check = !state.status.isSdkReady &&
        !!state.modifications &&
        state.modifications.size > 0;
    const flags = {};
    if (check) {
        params.forEach((item) => {
            var _a;
            const modification = (_a = state.modifications) === null || _a === void 0 ? void 0 : _a.get(item.key);
            if (modification && checkType(modification === null || modification === void 0 ? void 0 : modification.value, item.defaultValue)) {
                flags[item.key] = modification.value;
            }
            else {
                flags[item.key] = item.defaultValue;
            }
        });
        return flags;
    }
    logWarn(config, noVisitorDefault, functionName);
    params.forEach((item) => {
        flags[item.key] = item.defaultValue;
    });
    return flags;
};
/**
 * Retrieve a modification value by its key. If no modification match the given key or if the stored value type and default value type do not match, default value will be returned.
 * @deprecated use useFsFlag instead
 */
export const useFsModifications = (params, activateAll) => {
    const { state } = useContext(FlagshipContext);
    const { visitor, config } = state;
    const functionName = 'useFsModifications';
    return fsModificationsSync({
        functionName,
        state,
        visitor,
        config,
        params,
        activateAll
    });
};
/**
 * Retrieve a modification value by its key. If no modification match the given key or if the stored value type and default value type do not match, default value will be returned.
 * @deprecated use useFsFlag instead
 */
export const useFsModification = (params) => {
    var _a;
    const { state } = useContext(FlagshipContext);
    const { visitor, config } = state;
    const functionName = 'useFsModifications';
    if (visitor) {
        return visitor.getModificationSync(params);
    }
    const modification = (_a = state.modifications) === null || _a === void 0 ? void 0 : _a.get(params.key);
    if (!state.status.isSdkReady &&
        modification &&
        checkType(modification === null || modification === void 0 ? void 0 : modification.value, params.defaultValue)) {
        return modification.value;
    }
    logWarn(config, noVisitorDefault, functionName);
    return params.defaultValue;
};
const fsModificationInfoSync = (args) => {
    var _a;
    const { key, visitor, state } = args;
    if (visitor) {
        return visitor.getModificationInfoSync(key);
    }
    const modification = (_a = state.modifications) === null || _a === void 0 ? void 0 : _a.get(key);
    if (!state.status.isSdkReady && modification) {
        return modification;
    }
    return null;
};
/**
 * Get the campaign modification information value matching the given key.
 * @param {string} key key which identify the modification.
 * @deprecated use useFsFlag instead
 */
export const useFsModificationInfo = (key) => {
    const { state } = useContext(FlagshipContext);
    const { visitor } = state;
    return fsModificationInfoSync({ key, state, visitor });
};
const fsActivate = (params, functionName, visitor, config) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        if (!visitor) {
            logWarn(config, noVisitorMessage, functionName);
            return;
        }
        yield visitor.activateModifications(params);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }
    catch (error) {
        logWarn(config, error.message || error, functionName);
    }
});
/**
 * This hook returns a flag object by its key. If no flag match the given key an empty flag will be returned.
 * @param key
 * @param defaultValue
 * @returns
 */
export const useFsFlag = (key, defaultValue) => {
    const { state } = useContext(FlagshipContext);
    const { visitor } = state;
    if (!visitor) {
        return new Flag(defaultValue, key, state.modifications);
    }
    return visitor.getFlag(key, defaultValue);
};
/**
 * Report this user has seen this modification. Report this user has seen these modifications.
 * @param params
 * @deprecated use useFsFlag instead
 * @returns
 */
export const useFsActivate = (params) => __awaiter(void 0, void 0, void 0, function* () {
    const { state } = useContext(FlagshipContext);
    const { visitor, config } = state;
    const functionName = 'useFsModifications';
    yield fsActivate(params, functionName, visitor, config);
});
export const useFlagship = () => {
    const { state } = useContext(FlagshipContext);
    const { visitor, config } = state;
    const fsUpdateContext = (context) => {
        const functionName = 'updateContext';
        if (!visitor) {
            logError(config, noVisitorMessage, functionName);
            return;
        }
        visitor.clearContext();
        visitor.updateContext(context);
    };
    const fsClearContext = () => {
        const functionName = 'cleanContext';
        if (!visitor) {
            logError(config, noVisitorMessage, functionName);
            return;
        }
        visitor.clearContext();
    };
    const fsAuthenticate = (visitorId) => {
        const functionName = 'authenticate';
        if (!visitor) {
            logError(config, noVisitorMessage, functionName);
            return;
        }
        visitor.authenticate(visitorId);
    };
    const fsUnauthenticate = () => {
        const functionName = 'unauthenticate';
        if (!visitor) {
            logError(config, noVisitorMessage, functionName);
            return;
        }
        visitor.unauthenticate();
    };
    /**
     * Send a Hit to Flagship servers for reporting.
     */
    const fsSendHit = (hit) => {
        const functionName = 'sendHit';
        if (!visitor) {
            logError(config, noVisitorMessage, functionName);
            return Promise.resolve();
        }
        return visitor.sendHit(hit);
    };
    /**
     * Send a Hit to Flagship servers for reporting.
     */
    const fsSendHits = (hit) => {
        const functionName = 'sendHits';
        if (!visitor) {
            logError(config, noVisitorMessage, functionName);
            return Promise.resolve();
        }
        return visitor.sendHits(hit);
    };
    let modifications = visitor === null || visitor === void 0 ? void 0 : visitor.getModificationsArray();
    if (!state.status.isSdkReady && state.modifications) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        modifications = Array.from(state.modifications, ([_key, item]) => item);
    }
    const activateModification = (params) => __awaiter(void 0, void 0, void 0, function* () {
        const functionName = 'activateModification';
        yield fsActivate(params, functionName, visitor, config);
    });
    const synchronizeModifications = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!visitor) {
            logWarn(config, noVisitorMessage, 'synchronizeModifications');
            return;
        }
        yield visitor.synchronizeModifications();
    });
    const getModifications = (params, activateAll) => {
        const functionName = 'getModifications';
        return fsModificationsSync({
            functionName,
            state,
            visitor,
            config,
            params,
            activateAll
        });
    };
    const getModificationInfo = (key) => {
        return fsModificationInfoSync({ key, state, visitor });
    };
    function getFlag(key, defaultValue) {
        if (!visitor) {
            return new Flag(defaultValue, key, state.modifications);
        }
        return visitor.getFlag(key, defaultValue);
    }
    function fetchFlags() {
        if (!visitor) {
            logWarn(config, noVisitorMessage, 'fetchFlags');
            return Promise.resolve();
        }
        return visitor.fetchFlags();
    }
    function setConsent(hasConsented) {
        if (!visitor) {
            logWarn(config, noVisitorMessage, 'setConsent');
            return;
        }
        visitor.setConsent(hasConsented);
    }
    function close() {
        return __awaiter(this, void 0, void 0, function* () {
            yield Flagship.close();
        });
    }
    return {
        visitorId: visitor === null || visitor === void 0 ? void 0 : visitor.visitorId,
        anonymousId: visitor === null || visitor === void 0 ? void 0 : visitor.anonymousId,
        context: Object.assign({}, visitor === null || visitor === void 0 ? void 0 : visitor.context),
        hasConsented: visitor === null || visitor === void 0 ? void 0 : visitor.hasConsented,
        setConsent,
        updateContext: fsUpdateContext,
        clearContext: fsClearContext,
        authenticate: fsAuthenticate,
        unauthenticate: fsUnauthenticate,
        status: state.status,
        activateModification,
        synchronizeModifications,
        getModifications,
        modifications: modifications || [],
        flagsData: (visitor === null || visitor === void 0 ? void 0 : visitor.getFlagsDataArray()) || [],
        getModificationInfo,
        hit: {
            send: fsSendHit,
            sendMultiple: fsSendHits
        },
        getFlag,
        fetchFlags,
        close
    };
};
